/* Copyright (c) 2020-present CloudIO Inc */

import { getManager } from '../../../componentregistry/registry/Manager';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionBatch';

const ActionBatchManager: ActionManagerFn<'ActionBatch'> = (
  platform: Platform,
): ActionManager<'ActionBatch'> => {
  return {
    ...baseActionManager(platform, 'ActionBatch'),
    isChildAllowed: (page, item, childType) =>
      getManager(childType).managerType === 'Action',
    defaultValue,
    props: {
      ...baseActionProps,
    },
    name: 'Batch Actions',
    icon: { iconName: 'tasks-alt' },
    description: `**Overview**<br/>
The Batch Actions component serves as a logical container and performance optimization tool designed to group multiple operational steps. Its primary function is to execute a sequence of child actions as a single cohesive unit, specifically ensuring that the user interface does not re-render multiple times during the process.<br/><br/>
This component acts as a wrapper that accepts any standard Action component as a child. By consolidating execution, it prevents intermediate UI updates, resulting in smoother visual transitions and improved application performance during complex logic flows.

**Key Features**<br/>
* **Rendering Optimization:** Explicitly configured to "avoid multiple rendering," stabilizing the UI by deferring updates until all nested actions have completed.
* **Universal Action Containment:** The isChildAllowed function dynamically validates children, permitting any component with a manager type of 'Action' to be nested inside. This allows for flexible grouping of any available action types.
* **Visual Grouping:** Represented by the tasks-alt icon, aiding in the visual organization of complex logic within the action registry.

**Properties**<br/>
* **Base Properties:** Utilizes the standard ...baseActionProps configuration, inheriting common action behaviors without introducing specific parameters or settings for the batching mechanism itself.

**Use Cases**<br/>
* **Performance Tuning:** Grouping multiple variable assignment actions or state changes that would otherwise cause the UI to flicker or refresh multiple times in rapid succession.
* **Logic Organization:** Structuring unrelated or sequential actions into a single block to improve readability in the editor.`,
  };
};

export default ActionBatchManager;
